<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { publicTrialsApi } from '../../api/publicTrials';

const router = useRouter();
const templates = ref([]);
const loadingTemplates = ref(true);
const selectedTemplate = ref(null);
const submitting = ref(false);
const errorMessage = ref(null);

const form = ref({
  name: '',
  email: '',
  phone: '',
});

const canSubmit = computed(
  () =>
    selectedTemplate.value &&
    form.value.name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.value.email) &&
    !submitting.value
);

onMounted(async () => {
  try {
    const { data } = await publicTrialsApi.templates();
    templates.value = data.templates;
  } catch (e) {
    errorMessage.value =
      'Nao foi possivel carregar os setores. Tente recarregar.';
  } finally {
    loadingTemplates.value = false;
  }
});

const selectTemplate = template => {
  selectedTemplate.value = template;
  errorMessage.value = null;
  setTimeout(() => {
    document
      .getElementById('trial-form')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 50);
};

const handleSubmit = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  errorMessage.value = null;
  try {
    const { data } = await publicTrialsApi.createTrial({
      template_key: selectedTemplate.value.key,
      name: form.value.name,
      email: form.value.email,
      phone: form.value.phone,
    });
    router.push({
      name: 'trial_status',
      params: { token: data.trial_token },
    });
  } catch (e) {
    errorMessage.value =
      e?.response?.data?.error ||
      'Erro ao criar seu teste gratuito. Tente novamente.';
  } finally {
    submitting.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="min-h-screen bg-n-background text-n-slate-12">
    <!-- Hero -->
    <section class="max-w-5xl mx-auto px-6 pt-16 pb-12 text-center">
      <div
        class="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
      >
        <span class="size-1.5 rounded-full bg-n-brand" />
        Teste gratis por 7 dias — 50 conversas com IA inclusas
      </div>
      <h1
        class="text-4xl md:text-5xl font-bold tracking-tight text-n-slate-12 mb-4"
      >
        Seu atendente virtual,
        <span class="text-n-brand">treinado pro seu negocio</span>
      </h1>
      <p class="text-lg text-n-slate-11 max-w-2xl mx-auto">
        Escolha o setor, conte um pouco sobre voce e converse em segundos com um
        Humano Digital ja configurado pro seu mercado. Sem cartao, sem
        compromisso.
      </p>
    </section>

    <!-- Templates grid -->
    <section class="max-w-5xl mx-auto px-6 pb-12">
      <h2
        class="text-sm font-semibold uppercase tracking-wider text-n-slate-10 mb-4"
      >
        1. Escolha o setor do seu negocio
      </h2>

      <div v-if="loadingTemplates" class="text-center py-12 text-n-slate-10">
        Carregando setores...
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          v-for="t in templates"
          :key="t.key"
          type="button"
          class="text-left p-5 rounded-xl border transition-all duration-150 hover:-translate-y-0.5"
          :class="
            selectedTemplate?.key === t.key
              ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand'
              : 'border-n-weak bg-n-solid-1 hover:border-n-brand/50'
          "
          @click="selectTemplate(t)"
        >
          <div class="flex items-start justify-between gap-3 mb-2">
            <div class="text-base font-semibold text-n-slate-12">
              {{ t.sector }}
            </div>
            <span
              v-if="selectedTemplate?.key === t.key"
              class="size-5 rounded-full bg-n-brand text-white flex items-center justify-center text-xs"
            >
              ✓
            </span>
          </div>
          <div class="text-sm text-n-slate-11 mb-3">{{ t.hd_name }}</div>
          <p class="text-xs text-n-slate-10 leading-relaxed">
            {{ t.description }}
          </p>
        </button>
      </div>
    </section>

    <!-- Form -->
    <section
      v-if="selectedTemplate"
      id="trial-form"
      class="max-w-2xl mx-auto px-6 pb-20"
    >
      <h2
        class="text-sm font-semibold uppercase tracking-wider text-n-slate-10 mb-4"
      >
        2. Conte um pouco sobre voce
      </h2>

      <form
        class="bg-n-solid-1 rounded-2xl border border-n-weak p-6 space-y-4"
        @submit.prevent="handleSubmit"
      >
        <div>
          <label
            for="trial-name"
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
          >
            Seu nome *
          </label>
          <input
            id="trial-name"
            v-model="form.name"
            type="text"
            placeholder="Joao Silva"
            class="w-full px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            required
          />
        </div>

        <div>
          <label
            for="trial-email"
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
          >
            Seu email *
          </label>
          <input
            id="trial-email"
            v-model="form.email"
            type="email"
            placeholder="joao@suaempresa.com.br"
            class="w-full px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            WhatsApp <span class="text-n-slate-10 font-normal">(opcional)</span>
          </label>
          <input
            v-model="form.phone"
            type="tel"
            placeholder="+55 11 99999-9999"
            class="w-full px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </div>

        <div
          v-if="errorMessage"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-6 rounded-lg px-3 py-2"
        >
          {{ errorMessage }}
        </div>

        <button
          type="submit"
          :disabled="!canSubmit"
          class="w-full py-3 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{
            submitting
              ? 'Criando seu Humano Digital...'
              : 'Comecar teste gratis →'
          }}
        </button>

        <p class="text-xs text-n-slate-10 text-center">
          Voce recebera 50 interacoes gratis por 7 dias. Sem cartao, sem
          renovacao automatica.
        </p>
      </form>
    </section>
  </div>
</template>
